import { InfoM } from '@app/modules/audit/modules/aod/screens/aod-rule-view/components/InfoM';
import { AuditMatch, ScheduleStatusReportItem } from '@mi-front/core';
import { QueryView } from '@mi-front/ui';
import { Tooltip } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Chip, Dialog, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import { UseQueryResult } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { SimpleTable } from '../../../../../../components/base/SimpleTable';
import { LDateTime } from '../../../../../../components/date/LDateTime';
import { NoAudioIndicator } from '../../../../components/schedule/NoAudioIndicator';
import { ScheduleMatchStatus } from '../../../../components/schedule/ScheduleMatchStatus';

export function ScheduleStatusDetailDialog(p: {
  item: ScheduleStatusReportItem | null;
  matchesQuery: UseQueryResult<readonly AuditMatch[], unknown>;
  onHide: () => void;
}) {
  if (!p.item) {
    return null;
  }

  const title = `Status ${p.item.customId}`;

  const { matchesCount, matchesWithScheduleCount, schedulesCount } = p.item.status;

  return (
    <Dialog open={!!p.item} onClose={p.onHide} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container>
            <Grid item xs={12}>
              <InfoM
                label={
                  <FormattedMessage
                    id="audit.scheduleStatusScreen.detailModal.audio"
                    defaultMessage="Media"
                  />
                }
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>
                    <FormattedMessage
                      id="audit.scheduleStatus.detail.material"
                      defaultMessage="Material"
                    />{' '}
                    <span style={{ fontWeight: 500 }}>{p.item?.customId}</span>
                  </span>
                  {!p.item.audio && <NoAudioIndicator />}
                </Box>
              </InfoM>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <InfoM
                label={
                  <FormattedMessage
                    id="audit.scheduleStatusScreen.detailModal.status"
                    defaultMessage="Status"
                  />
                }
              >
                <SimpleTable
                  columns={[
                    {
                      header: {
                        content: (
                          <FormattedMessage
                            id="audit.scheduleStatusScreen.detailModal.matchesCount"
                            defaultMessage="# matches"
                          />
                        ),
                      },
                      headerProps: { sx: { width: '33%' } },
                      renderCell: (row) => ({
                        content: (
                          <Typography
                            sx={{
                              color: row.matchesCount === 0 ? 'error.main' : 'inherit',
                              fontWeight: row.matchesCount === 0 ? 'bold' : 'normal',
                            }}
                          >
                            {row.matchesCount}
                          </Typography>
                        ),
                      }),
                    },
                    {
                      header: {
                        content: (
                          <FormattedMessage
                            id="audit.scheduleStatusScreen.detailModal.matchesWithScheduleCount"
                            defaultMessage="# matches con schedule"
                          />
                        ),
                      },
                      headerProps: { sx: { width: '33%' } },
                      renderCell: (row) => row.matchesWithScheduleCount,
                    },
                    {
                      header: {
                        content: (
                          <FormattedMessage
                            id="audit.scheduleStatusScreen.detailModal.schedulesCount"
                            defaultMessage="# schedules"
                          />
                        ),
                      },
                      headerProps: { sx: { width: '33%' } },
                      renderCell: (row) => row.schedulesCount,
                    },
                  ]}
                  data={[
                    {
                      matchesCount,
                      matchesWithScheduleCount,
                      schedulesCount,
                    },
                  ]}
                />
              </InfoM>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <InfoM
                label={
                  <FormattedMessage
                    id="audit.scheduleStatusScreen.detailModal.schedules"
                    defaultMessage="Schedules"
                  />
                }
              >
                <SimpleTable
                  columns={[
                    {
                      header: {
                        content: (
                          <FormattedMessage
                            id="audit.props.datetime"
                            defaultMessage="Fecha y hora programada"
                          />
                        ),
                      },
                      headerProps: { sx: { width: '40%' } },
                      renderCell: (schedule) => ({
                        content: <LDateTime value={schedule.datetime} />,
                      }),
                    },
                    {
                      header: {
                        content: (
                          <FormattedMessage
                            id="audit.scheduleStatusScreen.detailModal.matches"
                            defaultMessage="Matches"
                          />
                        ),
                      },
                      headerProps: { align: 'center' },
                      renderCell: (schedule) => ({
                        content: <ScheduleMatchStatus schedule={schedule} />,
                        props: { align: 'center' },
                      }),
                    },
                  ]}
                  data={p.item!.schedules}
                />
              </InfoM>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <InfoM
                label={
                  <FormattedMessage
                    id="audit.scheduleStatusScreen.detailModal.matches"
                    defaultMessage="Matches"
                  />
                }
              >
                <QueryView query={p.matchesQuery} inline>
                  {(matches) => {
                    if (!matches || !matches.length) {
                      return (
                        <Typography sx={{ color: 'text.secondary' }}>
                          <FormattedMessage
                            id="audit.scheduleStatusScreen.detailModal.noMatches"
                            defaultMessage="Sin matches"
                          />
                        </Typography>
                      );
                    }

                    return (
                      <SimpleTable
                        columns={[
                          {
                            header: {
                              content: (
                                <FormattedMessage
                                  id="audit.scheduleStatusScreen.detailModal.matchDatetime"
                                  defaultMessage="Fecha y hora emitida"
                                />
                              ),
                            },
                            headerProps: { sx: { width: '40%' } },
                            renderCell: (match) => ({
                              content: <LDateTime value={match.datetime} />,
                            }),
                          },
                          {
                            header: {
                              content: (
                                <FormattedMessage
                                  id="audit.scheduleStatusScreen.detailModal.matchedSchedule"
                                  defaultMessage="Schedule c/match"
                                />
                              ),
                            },
                            headerProps: { align: 'center' },
                            renderCell: (match) => {
                              const scheduleTime = match.matchedSchedule ? (
                                <LDateTime value={match.matchedSchedule.datetime} />
                              ) : null;

                              return {
                                content: match.matchedSchedule ? (
                                  <Tooltip
                                    title={
                                      <FormattedMessage
                                        id="audit.scheduleStatusScreen.detailModal.offTolerance"
                                        defaultMessage="La confirmación de schedule está fuera de la tolerancia permitida"
                                      />
                                    }
                                    placement="left"
                                    disableHoverListener={
                                      match.matchedScheduleConfirmationType !== 'OFF_TOLERANCE'
                                    }
                                    disableFocusListener={
                                      match.matchedScheduleConfirmationType !== 'OFF_TOLERANCE'
                                    }
                                  >
                                    <Chip
                                      label={scheduleTime}
                                      size="small"
                                      color={
                                        match.matchedScheduleConfirmationType === 'DEFAULT'
                                          ? 'primary'
                                          : 'warning'
                                      }
                                      icon={
                                        match.matchedScheduleConfirmationType ===
                                        'OFF_TOLERANCE' ? (
                                          <WarningIcon />
                                        ) : undefined
                                      }
                                    />
                                  </Tooltip>
                                ) : null,
                                props: { align: 'center' },
                              };
                            },
                          },
                        ]}
                        data={matches}
                      />
                    );
                  }}
                </QueryView>
              </InfoM>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
